import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Checkbox,
  Grid,
  IconButton,
  Divider,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { ReferralData } from './types';
import { DeduplicationMatch } from './Table/DeduplicationTable';

interface MergeComparisonModalProps {
  open: boolean;
  onClose: () => void;
  sourceRecord: ReferralData;
  targetRecord: DeduplicationMatch;
  onMerge: (
    selectedFields: string[],
    setSelectedFields: (fields: string[]) => void,
    onClose: () => void,
    confirmationModalMessages?: any
  ) => void;
  confirmationModalMessages?: any;
  isSending?: boolean;
}

interface ComparisonField {
  key: string;
  label: string;
  sourceValue: string;
  targetValue: string;
}

const MergeComparisonModal: React.FC<MergeComparisonModalProps> = ({
  open,
  onClose,
  sourceRecord,
  targetRecord,
  onMerge,
  confirmationModalMessages,
  isSending = false,
}) => {
  const getMatchingPhoneNumber = (): string => {
    const cleanSourcePhone = sourceRecord.phone?.replace(/\D/g, '') || '';

    if (!cleanSourcePhone) {
      return '-';
    }

    const phoneFields = ['phone1', 'phone2', 'phone3', 'phone4'];

    for (const phoneField of phoneFields) {
      const phoneObj = (targetRecord as any)[phoneField];
      if (phoneObj?.rawNumber) {
        const cleanRawNumber = phoneObj.rawNumber.replace(/\D/g, '');
        if (cleanSourcePhone === cleanRawNumber) {
          const digits = phoneObj.rawNumber.replace(/\D/g, '');
          if (digits.length === 10) {
            return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
          }
          return phoneObj.rawNumber || phoneObj.number;
        }
      }
    }

    for (const phoneField of phoneFields) {
      const phoneObj = (targetRecord as any)[phoneField];
      if (phoneObj?.number && !phoneObj?.outOfService) {
        const digits = phoneObj.number.replace(/\D/g, '');
        if (digits.length === 10) {
          return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
        }
        return phoneObj.number;
      }
    }

    if (targetRecord.phoneNumber) {
      const digits = targetRecord.phoneNumber.replace(/\D/g, '');
      if (digits.length === 10) {
        return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
      }
      return targetRecord.phoneNumber;
    }

    return '-';
  };

  const formatNumber = (phoneObj: any): string => {
    const digits = phoneObj.replace(/\D/g, '');
    if (digits.length === 10) {
      return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    return digits || '-';
  };

  // Helper function to get UTM field value from targetRecord customFields
  const getCustomFieldValue = (fieldName: string): string => {
    const customFields = (targetRecord as any)?.customFields;
    if (!customFields || !Array.isArray(customFields)) {
      return '-';
    }
    const field = customFields.find((f: any) => f.name === fieldName);
    return field?.value || '-';
  };

  const formatDateOfBirth = (dateString: string | undefined): string => {
    if (!dateString) return '-';

    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      return dateString;
    }

    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return dateString;
      }

      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');

      return `${year}-${month}-${day}`;
    } catch (error) {
      return dateString;
    }
  };

  const getInitialSelectedFields = () => {
    const fields: string[] = [];
    const allFields: ComparisonField[] = [
      {
        key: 'firstName',
        label: 'First Name',
        sourceValue: sourceRecord.firstName || '-',
        targetValue: targetRecord.firstName || '-',
      },
      {
        key: 'lastName',
        label: 'Last Name',
        sourceValue: sourceRecord.lastName || '-',
        targetValue: targetRecord.lastName || '-',
      },
      {
        key: 'dob',
        label: 'Date of Birth',
        sourceValue: sourceRecord.dob || '-',
        targetValue: targetRecord.dateOfBirth || '-',
      },
      {
        key: 'gender',
        label: 'Gender',
        sourceValue: sourceRecord.gender || '-',
        targetValue:
          targetRecord.genderCode === 'M'
            ? 'Male'
            : targetRecord.genderCode === 'F'
            ? 'Female'
            : '-',
      },
      {
        key: 'phoneNumber',
        label: 'Phone Number',
        sourceValue: formatNumber(sourceRecord.phone || ''),
        targetValue: getMatchingPhoneNumber(),
      },
      {
        key: 'email',
        label: 'Email',
        sourceValue: sourceRecord.email || '-',
        targetValue: targetRecord.primaryEmail?.email || '-',
      },
      {
        key: 'address',
        label: 'Address',
        sourceValue: sourceRecord.address || '-',
        targetValue: targetRecord.address1 || '-',
      },
      {
        key: 'city',
        label: 'City',
        sourceValue: sourceRecord.city || '-',
        targetValue: targetRecord.city || '-',
      },
      {
        key: 'state',
        label: 'State',
        sourceValue: sourceRecord.stateCode || '-',
        targetValue: targetRecord.state || '-',
      },
      {
        key: 'zipcode',
        label: 'Zipcode',
        sourceValue: sourceRecord.zipcode || '-',
        targetValue: targetRecord.zip || '-',
      },
      {
        key: 'utm_source',
        label: 'UTM Source',
        sourceValue: sourceRecord.utm_source || '-',
        targetValue: getCustomFieldValue('Avacare Website - UTM Campaign Source'),
      },
      {
        key: 'utm_medium',
        label: 'UTM Medium',
        sourceValue: sourceRecord.utm_medium || '-',
        targetValue: getCustomFieldValue('Avacare Website - UTM Campaign Medium'),
      },
      {
        key: 'utm_campaign',
        label: 'UTM Campaign',
        sourceValue: sourceRecord.utm_campaign || '-',
        targetValue: getCustomFieldValue('Avacare Website - UTM Campaign Name'),
      },
      {
        key: 'utm_term',
        label: 'UTM Term',
        sourceValue: sourceRecord.utm_term || '-',
        targetValue: getCustomFieldValue('Avacare Website - UTM Campaign Term'),
      },
      {
        key: 'utm_content',
        label: 'UTM Content',
        sourceValue: sourceRecord.utm_content || '-',
        targetValue: getCustomFieldValue('Avacare Website - UTM Campaign Content'),
      },
      {
        key: 'sID',
        label: 'sID',
        sourceValue: sourceRecord.sID || '-',
        targetValue: getCustomFieldValue('Avacare Website - sID'),
      },
      {
        key: 'psecID',
        label: 'psecID',
        sourceValue: sourceRecord.psecID || '-',
        targetValue: getCustomFieldValue('Avacare Website - psecID'),
      },
      {
        key: 'atid',
        label: 'ATID',
        sourceValue: sourceRecord.atid || '-',
        targetValue: getCustomFieldValue('Avacare Website - UTM ATID'),
      },
      {
        key: 'gclid',
        label: 'GCLID',
        sourceValue: sourceRecord.gclid || '-',
        targetValue: getCustomFieldValue('Avacare Website - UTM GCLID'),
      },
      {
        key: 'fbclid',
        label: 'FBCLID',
        sourceValue: sourceRecord.fbclid || '-',
        targetValue: getCustomFieldValue('Avacare Website - UTM FBCLID'),
      },
      {
        key: 'emailOptInDate',
        label: 'Email Opt-In Date',
        sourceValue: '-',
        targetValue: '-',
      },
      {
        key: 'isSecondaryContactMediumEmail',
        label: 'Email Contact Medium',
        sourceValue: sourceRecord.secondaryConsentinfo?.isSecondaryContactMediumEmail
          ? 'Yes'
          : 'No',
        targetValue: '-',
      },
      {
        key: 'isSecondaryContactMediumSMS',
        label: 'SMS Contact Medium',
        sourceValue: sourceRecord.secondaryConsentinfo?.isSecondaryContactMediumSMS ? 'Yes' : 'No',
        targetValue: '-',
      },
      {
        key: 'smsOptInDate',
        label: 'SMS Opt-In Date',
        sourceValue: '-',
        targetValue: '-',
      },
    ];

    allFields.forEach((field) => {
      const sourceHasValue = field.sourceValue !== '-';
      const targetHasValue = field.targetValue !== '-';
      // Auto-select fields where source has value but target doesn't (green background)
      if (sourceHasValue && !targetHasValue) {
        fields.push(field.key);
      }
    });

    return fields;
  };

  const [selectedFields, setSelectedFields] = useState<string[]>(getInitialSelectedFields());
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  const comparisonFields: ComparisonField[] = [
    {
      key: 'firstName',
      label: 'First Name',
      sourceValue: sourceRecord.firstName || '-',
      targetValue: targetRecord.firstName || '-',
    },
    {
      key: 'lastName',
      label: 'Last Name',
      sourceValue: sourceRecord.lastName || '-',
      targetValue: targetRecord.lastName || '-',
    },
    {
      key: 'dob',
      label: 'Date of Birth',
      sourceValue: sourceRecord.dob || '-',
      targetValue: formatDateOfBirth(targetRecord.dateOfBirth),
    },
    {
      key: 'gender',
      label: 'Gender',
      sourceValue: sourceRecord.gender || '-',
      targetValue: targetRecord.genderCode === 'M' ? 'Male' : 'Female',
    },
    {
      key: 'phoneNumber',
      label: 'Phone Number',
      sourceValue: formatNumber(sourceRecord.phone || ''),
      targetValue: getMatchingPhoneNumber(),
    },
    {
      key: 'email',
      label: 'Email',
      sourceValue: sourceRecord.email || '-',
      targetValue: targetRecord.primaryEmail?.email || '-',
    },
    {
      key: 'address',
      label: 'Address',
      sourceValue: sourceRecord.address || '-',
      targetValue: targetRecord.address1 || '-',
    },
    {
      key: 'city',
      label: 'City',
      sourceValue: sourceRecord.city || '-',
      targetValue: targetRecord.city || '-',
    },
    {
      key: 'state',
      label: 'State',
      sourceValue: sourceRecord.stateCode || '-',
      targetValue: targetRecord.state || '-',
    },
    {
      key: 'zipcode',
      label: 'Zipcode',
      sourceValue: sourceRecord.zipcode || '-',
      targetValue: targetRecord.zip || '-',
    },
    {
      key: 'utm_source',
      label: 'UTM Source',
      sourceValue: sourceRecord.utm_source || '-',
      targetValue: getCustomFieldValue('Avacare Website - UTM Campaign Source'),
    },
    {
      key: 'utm_medium',
      label: 'UTM Medium',
      sourceValue: sourceRecord.utm_medium || '-',
      targetValue: getCustomFieldValue('Avacare Website - UTM Campaign Medium'),
    },
    {
      key: 'utm_campaign',
      label: 'UTM Campaign',
      sourceValue: sourceRecord.utm_campaign || '-',
      targetValue: getCustomFieldValue('Avacare Website - UTM Campaign Name'),
    },
    {
      key: 'utm_term',
      label: 'UTM Term',
      sourceValue: sourceRecord.utm_term || '-',
      targetValue: getCustomFieldValue('Avacare Website - UTM Campaign Term'),
    },
    {
      key: 'utm_content',
      label: 'UTM Content',
      sourceValue: sourceRecord.utm_content || '-',
      targetValue: getCustomFieldValue('Avacare Website - UTM Campaign Content'),
    },
    {
      key: 'sID',
      label: 'sID',
      sourceValue: sourceRecord.sID || '-',
      targetValue: getCustomFieldValue('Avacare Website - sID'),
    },
    {
      key: 'psecID',
      label: 'PSEC ID',
      sourceValue: sourceRecord.psecID || '-',
      targetValue: getCustomFieldValue('Avacare Website - psecID'),
    },
    {
      key: 'atid',
      label: 'AT ID',
      sourceValue: sourceRecord.atid || '-',
      targetValue: getCustomFieldValue('Avacare Website - UTM ATID'),
    },
    {
      key: 'gclid',
      label: 'Google Click ID',
      sourceValue: sourceRecord.gclid || '-',
      targetValue: getCustomFieldValue('Avacare Website - UTM GCLID'),
    },
    {
      key: 'fbclid',
      label: 'Facebook Click ID',
      sourceValue: sourceRecord.fbclid || '-',
      targetValue: getCustomFieldValue('Avacare Website - UTM FBCLID'),
    },
    {
      key: 'emailOptInDate',
      label: 'Email Opt-In Date',
      sourceValue: '-',
      targetValue: '-',
    },
    {
      key: 'isSecondaryContactMediumEmail',
      label: 'Email Contact Medium',
      sourceValue: sourceRecord.secondaryConsentinfo?.isSecondaryContactMediumEmail ? 'Yes' : 'No',
      targetValue: '-',
    },
    {
      key: 'isSecondaryContactMediumSMS',
      label: 'SMS Contact Medium',
      sourceValue: sourceRecord.secondaryConsentinfo?.isSecondaryContactMediumSMS ? 'Yes' : 'No',
      targetValue: '-',
    },
    {
      key: 'smsOptInDate',
      label: 'SMS Opt-In Date',
      sourceValue: '-',
      targetValue: '-',
    },
  ];

  const handleFieldToggle = (fieldKey: string) => {
    setSelectedFields((prev) =>
      prev.includes(fieldKey) ? prev.filter((key) => key !== fieldKey) : [...prev, fieldKey]
    );
  };

  const handleConfirmMerge = () => {
    onMerge(selectedFields, setSelectedFields, confirmationModalMessages, onClose);
  };

  const handleCancelConfirm = () => {
    setConfirmDialogOpen(false);
  };

  const handleCancel = () => {
    setSelectedFields([]);
    setConfirmDialogOpen(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="md" fullWidth>
      <DialogTitle sx={{ py: 0.75, px: 1.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" fontWeight={600} sx={{ fontSize: '16px' }}>
            Merge Comparison - CDP Profile
          </Typography>
          <IconButton onClick={handleCancel} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          p: 1.5,
          height: '500px',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box sx={{ mb: 0.75 }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '12px' }}>
            Select fields from matched record to override in source. Checked fields will be updated
            during merge.
          </Typography>
        </Box>

        {/* Header Row */}
        <Grid container spacing={1} sx={{ mb: 0.5 }}>
          <Grid item xs={3}>
            <Box
              sx={{
                p: 0.5,
                backgroundColor: '#f5f5f5',
                borderRadius: 1,
                textAlign: 'center',
              }}
            >
              <Typography variant="subtitle2" fontWeight={600} sx={{ fontSize: '12px' }}>
                Field
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={4}>
            <Box
              sx={{
                p: 0.5,
                backgroundColor: (theme: any) => theme.palette.primary?.light || '#E3F2FD',
                borderRadius: 1,
                textAlign: 'center',
              }}
            >
              <Typography variant="subtitle2" fontWeight={600} sx={{ fontSize: '12px' }}>
                Source Record (ID: {sourceRecord.id})
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={1}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
              }}
            >
              <Typography
                variant="caption"
                fontWeight={600}
                color="text.secondary"
                sx={{ fontSize: '9px' }}
              >
                Select
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={4}>
            <Box
              sx={{
                p: 0.5,
                backgroundColor: (theme: any) => theme.palette.success?.light || '#E8F5E9',
                borderRadius: 1,
                textAlign: 'center',
              }}
            >
              <Typography variant="subtitle2" fontWeight={600} sx={{ fontSize: '12px' }}>
                Matched Record (CC Ref: {targetRecord.ccPatientReferenceId})
              </Typography>
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ mb: 0.5 }} />

        <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', pr: 0.5 }}>
          {comparisonFields.map((field) => {
            const sourceHasValue = field.sourceValue !== '-';
            const targetHasValue = field.targetValue !== '-';
            const isSame = field.sourceValue === field.targetValue;
            const isSelected = selectedFields.includes(field.key);

            let sourceBackground = 'white';
            let showCheckbox = false;
            let isCheckboxDisabled = false;

            if (isSame) {
              sourceBackground = '#f5f5f5';
              showCheckbox = false;
            } else if (sourceHasValue && !targetHasValue) {
              sourceBackground = '#d4edda';
              showCheckbox = true;
              isCheckboxDisabled = true;
            } else if (sourceHasValue && targetHasValue && !isSame) {
              sourceBackground = '#fff3cd';
              showCheckbox = true;
              isCheckboxDisabled = false;
            }

            const targetBackground = isSame ? '#f5f5f5' : 'white';

            return (
              <Box key={field.key} sx={{ mb: 0.5 }}>
                <Grid container spacing={1} alignItems="center">
                  {/* Field Label */}
                  <Grid item xs={3}>
                    <Box
                      sx={{
                        py: 0.25,
                        px: 0.75,
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <Typography variant="body2" fontWeight={600} sx={{ fontSize: '12px' }}>
                        {field.label}
                      </Typography>
                    </Box>
                  </Grid>

                  {/* Source Value */}
                  <Grid item xs={4}>
                    <Box
                      sx={{
                        py: 0.25,
                        px: 0.75,
                        border: '1px solid #e0e0e0',
                        borderRadius: 1,
                        backgroundColor: sourceBackground,
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ wordBreak: 'break-word', fontSize: '12px' }}
                      >
                        {field.sourceValue}
                      </Typography>
                    </Box>
                  </Grid>

                  {/* Checkbox */}
                  <Grid item xs={1}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {showCheckbox && (
                        <Checkbox
                          checked={isSelected}
                          disabled={isCheckboxDisabled}
                          onChange={() => handleFieldToggle(field.key)}
                          size="small"
                          sx={{
                            color: (theme: any) => theme.palette.neutral5?.main,
                            '&.Mui-checked': {
                              color: (theme: any) => theme.palette.themeColor?.main || '#255E5E',
                            },
                            '&.Mui-disabled': {
                              color: (theme: any) => theme.palette.themeColor?.main || '#255E5E',
                              opacity: 0.75,
                            },
                            p: 0,
                          }}
                        />
                      )}
                    </Box>
                  </Grid>

                  {/* Matched Value */}
                  <Grid item xs={4}>
                    <Box
                      sx={{
                        py: 0.25,
                        px: 0.75,
                        border: '1px solid #e0e0e0',
                        borderRadius: 1,
                        backgroundColor: targetBackground,
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ wordBreak: 'break-word', fontSize: '12px' }}
                      >
                        {field.targetValue}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            );
          })}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 1, gap: 1 }}>
        <Button onClick={handleCancel} variant="outlined" size="small" color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleConfirmMerge}
          variant="contained"
          size="small"
          disabled={isSending}
          startIcon={isSending ? <CircularProgress size={16} color="inherit" /> : null}
          sx={{
            backgroundColor: (theme: any) => theme.palette.themeColor?.main || '#255E5E',
            '&:hover': {
              backgroundColor: (theme: any) => theme.palette.themeColor?.dark || '#1a4545',
            },
            '&.Mui-disabled': {
              backgroundColor: '#ccc',
              color: '#888',
            },
          }}
        >
          {isSending ? 'Merging...' : `Merge TO CC (${selectedFields.length})`}
        </Button>
      </DialogActions>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialogOpen} onClose={handleCancelConfirm} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ py: 0.75, px: 1.5 }}>
          <Typography variant="h6" fontWeight={600} sx={{ fontSize: '14px' }}>
            Confirm Merge
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ py: 1, px: 1.5 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: '12px' }}>
            Are you sure you want to merge the selected fields to Care Coordination?
          </Typography>
          <Box
            sx={{
              p: 1,
              backgroundColor: '#f5f5f5',
              borderRadius: 1,
              border: '1px solid #e0e0e0',
            }}
          >
            <Typography variant="body2" fontWeight={600} sx={{ mb: 0.25, fontSize: '11px' }}>
              Merge Summary:
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: '11px', lineHeight: 1.4 }}
            >
              • Source Record ID: <strong>{sourceRecord.id}</strong>
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: '11px', lineHeight: 1.4 }}
            >
              • Target CC Reference: <strong>{targetRecord.ccPatientReferenceId}</strong>
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: '11px', lineHeight: 1.4 }}
            >
              • Fields to merge: <strong>{selectedFields.length}</strong>
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 1, gap: 0.5 }}>
          <Button onClick={handleCancelConfirm} variant="outlined" size="small" color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmMerge}
            variant="contained"
            size="small"
            color="primary"
            sx={{
              backgroundColor: (theme: any) => theme.palette.themeColor?.main || '#255E5E',
              '&:hover': {
                backgroundColor: (theme: any) => theme.palette.themeColor?.dark || '#1a4545',
              },
            }}
          >
            Confirm Merge
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default MergeComparisonModal;
